import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import callAPI from '../../../../api';
import { getBase64 } from '../../../../../utils/file';
import formatErrorMessage from '../../../../../utils/message-error';

export const getCustomerById = createAsyncThunk<any, any>(
  'customer/getCustomerById',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}` });
    return data;
  },
);

export const getCustomerProfileById = createAsyncThunk<any, any>(
  'customer/getCustomerProfileById',
  async ({ customerId, callbackError, callbackSuccess }) => {
    try {
      const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/profiles` });
      callbackSuccess(data);
      return data;
    } catch (e) {
      callbackError(e);
      throw e;
    }
  },
);

export const createCustomerProfile = createAsyncThunk<any, any>(
  'customer/createCustomerProfile',
  async ({ payload, callbackError, callbackSuccess }) => {
    try {
      const response = await callAPI({
        method: 'POST',
        url: `customers/${payload.customer?.id}/profiles`,
        data: payload,
      });
      callbackSuccess(response);
    } catch (e) {
      callbackError(e);
    }
  },
);

export const getCustomerLocalizationsByCustomerId = createAsyncThunk(
  'customer/getCustomerLocalizationsByCustomerId',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/localizations` });
    return data;
  },
);

export const getDependsByCustomerId = createAsyncThunk<any, any>(
  'customer/getDependsByCustomerId',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/dependents` });
    return data;
  },
);

export const getContactsByCustomerId = createAsyncThunk<any, any>(
  'customer/getContactsByCustomerId',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/contacts` });
    return data;
  },
);

export const getBanksByCustomerId = createAsyncThunk<any, any>(
  'customer/getBanksByCustomerId',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/banks` });
    return data;
  },
);

export const getPatrimoniesByCustomerId = createAsyncThunk<any, any>(
  'customer/getPatrimoniesByCustomerId',
  async (customerId) => {
    const { data } = await callAPI({ method: 'GET', url: `customers/${customerId}/patrimonies` });
    return data;
  },
);

export const putCustomerById = createAsyncThunk<any, any>(
  'customer/putCustomerById',
  async ({ customer, callbackSuccess, callbackError }) => {
    const { customerId, payload } = customer;
    try {
      const payloadToResquest = {
        ...payload,
        professionId: payload?.profession?.id,
        birthplaceId: payload?.birthplace?.id,
        file: payload.file
          ? { content: await getBase64(payload.file), name: payload.file.name }
          : null,
      };
      await callAPI({ method: 'PUT', url: `customers/${customerId}`, data: payloadToResquest });
      callbackSuccess('Dados atualizados com sucesso !');
      return payload;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const deleteLocalizationById = createAsyncThunk<any, any>(
  'customer/deleteLocalizationById',
  async ({ localizationId, callbackError, callbackSuccess }) => {
    try {
      await callAPI({ method: 'DELETE', url: `localizations/${localizationId}` });
      callbackSuccess();
      return localizationId;
    } catch (error) {
      callbackError()(formatErrorMessage(error));
      throw error;
    }
  },
);

export const deleteDependentById = createAsyncThunk<any, any>(
  'customer/deleteDependentById',
  async ({ dependentId, callbackError, callbackSuccess }) => {
    try {
      await callAPI({ method: 'DELETE', url: `dependents/${dependentId}` });
      callbackSuccess();
      return dependentId;
    } catch (error) {
      callbackError()(formatErrorMessage(error));
      throw error;
    }
  },
);

export const deletePatrimonyById = createAsyncThunk<any, any>(
  'customer/deletePatrimonyById',
  async ({ patrimonyId, callbackError, callbackSuccess }) => {
    try {
      await callAPI({ method: 'DELETE', url: `patrimonies/${patrimonyId}` });
      callbackSuccess();
      return patrimonyId;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const deleteContactById = createAsyncThunk<any, any>(
  'customer/deleteContactById',
  async ({ contactId, callbackError, callbackSuccess }) => {
    try {
      await callAPI({ method: 'DELETE', url: `contacts/${contactId}` });
      callbackSuccess('Contato removido com sucesso !');
      return contactId;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const deleteBankById = createAsyncThunk('customer/deleteBankById', async (bankId) => {
  await callAPI({ method: 'DELETE', url: `customerbanks/${bankId}` });
  return bankId;
});

export const addContact = createAsyncThunk<any, any>('customer/addContact', async ({ payload }) => {
  const { data } = await callAPI({ method: 'POST', url: 'customers/contacts/', data: payload });
  return data;
});

export const addLocalization = createAsyncThunk<any, any>(
  'customer/addLocalization',
  async ({ localization, callbackError, callbackSuccess }) => {
    const payloadToResquest = {
      ...localization,
      taxpayerTypeId: localization.taxpayerType.id,
      cityId: localization.city.id,
    };
    try {
      const { data } = await callAPI({
        method: 'POST',
        url: `customers/localizations/`,
        data: {
          ...payloadToResquest,
        },
      });
      callbackSuccess('Localizações atualizadas com sucesso !');
      return { ...localization, id: data.id };
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const updatePatrimonyById = createAsyncThunk<any, any>(
  'customer/updatePatrimonyById',
  async ({ patrimony, callbackError, callbackSuccess }) => {
    try {
      await callAPI({
        method: 'PUT',
        url: `patrimonies/${patrimony.id}`,
        data: {
          ...patrimony,
          cityId: patrimony?.city?.id,
        },
      });
      callbackSuccess('Patrimônios atualizados com sucesso !');
      return patrimony;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const addPatrimony = createAsyncThunk<any, any>(
  'customer/addPatrimony',
  async ({ patrimony, customerId, callbackError, callbackSuccess }) => {
    try {
      const { data } = await callAPI({
        method: 'POST',
        url: `customers/patrimonies/`,
        data: {
          ...patrimony,
          cityId: patrimony?.city?.id,
          customerId,
        },
      });
      callbackSuccess('Patrimônio adicionado com sucesso !');
      return data;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const addDependent = createAsyncThunk<any, any>(
  'customer/addDependent',
  async ({ dependent, callbackError, callbackSuccess }) => {
    try {
      const { data } = await callAPI({
        method: 'POST',
        url: `customers/dependents/`,
        data: {
          ...dependent,
        },
      });
      callbackSuccess('Dependentes atualizados com sucesso !');
      return { ...dependent, id: data.id };
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export const addBank = createAsyncThunk<any, any>(
  'customer/addBank',
  async ({ bank, customerId }) => {
    const payloadToRequest = {
      bankId: bank.bank.id,
      customerId,
      ...bank,
    };
    const { data } = await callAPI({
      method: 'POST',
      url: `customers/banks/`,
      data: payloadToRequest,
    });
    return { ...data, bank: bank.bank };
  },
);

export interface File {
  name: string;
  content: string;
}

export interface UpdateContact {
  id: number;
  type: string;
  customerId: number;
  description: string;
  file: string | File | undefined;
}

export const updateContact = createAsyncThunk<any, any>(
  'customer/updateContact',
  async ({ contact }) => {
    await callAPI({
      method: 'PUT',
      url: `contacts/${contact.id}`,
      data: {
        ...contact,
        situation: isEqual('Ative', contact.situation),
      },
    });
    return contact;
  },
);

export const updateLocalization = createAsyncThunk<any, any>(
  'customer/updateLocalization',
  async ({ localization, callbackError, callbackSuccess }) => {
    const payloadToResquest = {
      ...localization,
      taxpayerTypeId: localization.taxpayerType.id,
      cityId: localization.city.id,
    };
    try {
      await callAPI({
        method: 'PUT',
        url: `localizations/${localization.id}`,
        data: payloadToResquest,
      });
      callbackSuccess('Localizações atualizadas com sucesso !');
      return localization;
    } catch (error) {
      callbackError(formatErrorMessage(error));
      throw error;
    }
  },
);

export interface FileState {
  name: string;
  content: string;
}

export interface CustomerState {
  localizations: any[];
  dependents: any[];
  profile: any;
  contacts: ContactState[];
  banks: BankState[];
  patrimonies: any[];
}

export interface ContactState {
  id: number | undefined;
  type: string;
  customerId: number;
  description: string;
  file: string | FileState | undefined;
}

export interface Bank {
  label: string;
  id: number;
}

export interface BankState {
  id: number | undefined;
  bank: Bank | undefined;
  agency: string;
  accountDigit: string;
  agencyDigit: string;
  account: string;
  note: string;
  operation: string;
  type: string;
  file: FileState | undefined;
}

const INITIAL_STATE: CustomerState = {
  localizations: [],
  dependents: [],
  contacts: [],
  banks: [],
  patrimonies: [],
  profile: {},
};

export const customerSlice = createSlice({
  name: 'customer',
  initialState: INITIAL_STATE,
  reducers: {
    setSuccessUndefined: (state) => {
      return { ...state, success: undefined };
    },
  },
  extraReducers: {
    [`${getCustomerById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        ...payload,
        rgUf: { label: payload?.rgUf?.name, id: payload?.rgUf?.id },
      };
    },
    [`${putCustomerById.fulfilled}`]: (state, { payload }) => {
      return { ...state, ...payload };
    },
    [`${putCustomerById.rejected}`]: (state, { payload }) => {
      return { ...state, ...payload };
    },
    [`${deleteDependentById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        dependents: state.dependents.filter((dependent: any) => dependent.id !== payload),
      };
    },
    [`${getCustomerLocalizationsByCustomerId.fulfilled}`]: (state, { payload }) => {
      const localizations = payload.map((localization: any) => {
        return {
          ...localization,
          city: {
            label: localization.city.name,
            id: localization.city.id,
            uf: localization.city.uf.name,
          },
        };
      });
      return { ...state, localizations: [...localizations] };
    },
    [`${getDependsByCustomerId.fulfilled}`]: (state, { payload }) => {
      return { ...state, dependents: [...payload] };
    },
    [`${getPatrimoniesByCustomerId.fulfilled}`]: (state, { payload }) => {
      return { ...state, patrimonies: [...payload] };
    },
    [`${getContactsByCustomerId.fulfilled}`]: (state, { payload }) => {
      return { ...state, contacts: [...payload] };
    },
    [`${getBanksByCustomerId.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        banks: [
          ...payload.map((bank: any) => {
            return { ...bank, bank: { label: bank.bankName, id: bank.bankId } };
          }),
        ],
      };
    },
    [`${deleteContactById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        contacts: state.contacts.filter((element: any) => element.id !== payload),
      };
    },
    [`${deleteBankById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        banks: state.banks.filter((element: any) => element.id !== payload),
      };
    },
    [`${deletePatrimonyById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        patrimonies: [...state.patrimonies.filter((element: any) => element.id !== payload)],
      };
    },
    [`${deleteLocalizationById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        localizations: [...state.localizations.filter((element: any) => element.id !== payload)],
      };
    },
    // eslint-disable-next-line no-unused-vars
    [`${updateLocalization.fulfilled}`]: (state, { payload }) => {
      // return {
      //   ...state, localizations: [...state.localizations.map(localization => localization.id === payload.id ? payload : localization)],
      // };
      return { ...state };
    },
    [`${addContact.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        contacts: state.contacts.concat(payload),
      };
    },
    [`${addBank.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        banks: state.banks.concat({
          ...payload,
        }),
      };
    },
    // eslint-disable-next-line no-unused-vars
    [`${addContact.rejected}`]: (state, { payload }) => {
      return {
        ...state,
      };
    },
    [`${addPatrimony.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        patrimonies: state.patrimonies.concat(payload),
      };
    },
    [`${addLocalization.fulfilled}`]: (state: any, { payload }: any) => {
      return {
        ...state,
        localizations: state.localizations.concat([payload]),
      };
    },
    // eslint-disable-next-line no-unused-vars
    [`${addLocalization.rejected}`]: (state, { payload }) => {
      return { ...state };
    },
    [`${updateContact.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        contacts: state.contacts.map((contact: any) =>
          isEqual(contact.id, payload.id) ? { ...payload } : contact,
        ),
      };
    },
    // eslint-disable-next-line no-unused-vars
    [`${updateContact.rejected}`]: (state, { payload }) => {
      return {
        ...state,
      };
    },
    [`${updatePatrimonyById.fulfilled}`]: (state: any, { payload }) => {
      return {
        ...state,
        patrimonies: state.patrimonies.map((patrimony: any) =>
          patrimony.id === payload.id ? payload : patrimony,
        ),
      };
    },
    [`${addDependent.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        dependents: state.dependents.concat(payload),
      };
    },
    [`${getCustomerProfileById.fulfilled}`]: (state, { payload }) => {
      return {
        ...state,
        profile: { ...state.profile, ...payload },
      };
    },
    [`${getCustomerProfileById.rejected}`]: (state, { error }) => {
      return {
        ...state,
        profile: null,
        error: error.message,
      };
    },
  },
});

export default customerSlice.reducer;

export const { setSuccessUndefined } = customerSlice.actions;
